import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from '@phosphor-icons/react';
import { initializeGA, sendPageview } from './utils/analytics';
import './App.css';

const CookiePopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Show popup after a short delay
      setTimeout(() => {
        setIsVisible(true);
      }, 1000);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setIsVisible(false);
    // Initialize GA and send initial pageview when user accepts
    initializeGA();
    sendPageview();
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    setIsVisible(false);
  };

  const handleLearnMore = (e) => {
    e.preventDefault();
    setIsPolicyModalOpen(true);
  };

  const closePolicyModal = () => {
    setIsPolicyModalOpen(false);
  };

  // Handle Escape key press for policy modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isPolicyModalOpen) {
        closePolicyModal();
      }
    };

    if (isPolicyModalOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isPolicyModalOpen]);

  return (
    <>
      <AnimatePresence>
        {isVisible && (
          <motion.div
            className="cookie-popup"
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <div className="cookie-popup-content">
            <p className="cookie-popup-text">
              We use cookies to enhance your browsing experience and analyze site traffic.{' '}
              <button 
                type="button"
                onClick={handleLearnMore} 
                className="cookie-popup-learn-more"
              >
                Learn more
              </button>
            </p>
              <div className="cookie-popup-buttons">
                <button 
                  className="cookie-popup-button cookie-popup-button-accept"
                  onClick={handleAccept}
                >
                  Accept
                </button>
                <button 
                  className="cookie-popup-button cookie-popup-button-decline"
                  onClick={handleDecline}
                >
                  Decline
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cookie Policy Modal */}
      <AnimatePresence>
        {isPolicyModalOpen && (
          <>
            <motion.div
              className="cookie-policy-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={closePolicyModal}
            />
            <motion.div
              className="cookie-policy-modal"
              initial={{ opacity: 0, scale: 0.9, x: '-50%', y: '-50%' }}
              animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
              exit={{ opacity: 0, scale: 0.9, x: '-50%', y: '-50%' }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              style={{
                top: '50%',
                left: '50%',
              }}
            >
              <button
                className="cookie-policy-close"
                onClick={closePolicyModal}
                aria-label="Close cookie policy"
              >
                <X size={24} weight="regular" />
              </button>
              <h2>Cookie Policy</h2>
              <div className="cookie-policy-content">
                <h3>What are cookies?</h3>
                <p>
                  Cookies are small text files that are placed on your device when you visit our website. 
                  They help us provide you with a better browsing experience and allow us to analyze how 
                  our site is being used.
                </p>
                
                <h3>How we use cookies</h3>
                <p>
                  We use cookies for the following purposes:
                </p>
                <ul>
                  <li><strong>Analytics:</strong> We use Google Analytics to understand how visitors interact with our website, 
                  which helps us improve our content and user experience.</li>
                  <li><strong>Functionality:</strong> Cookies help us remember your preferences and provide 
                  personalized features.</li>
                </ul>

                <h3>Your choices</h3>
                <p>
                  You can choose to accept or decline cookies. If you decline cookies, some features of 
                  our website may not function properly. You can change your cookie preferences at any time 
                  by clearing your browser's cookies and refreshing the page.
                </p>

                <h3>Third-party cookies</h3>
                <p>
                  We may use third-party services (such as Google Analytics) that set their own cookies. 
                  These cookies are subject to the privacy policies of those third parties.
                </p>

                <h3>More information</h3>
                <p>
                  For more information about cookies and how to manage them, please visit{' '}
                  <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer">
                    www.allaboutcookies.org
                  </a>
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default CookiePopup;

